import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {MarkdownViewer} from '@github-ui/markdown-viewer'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {InfoIcon} from '@primer/octicons-react'
import {Button, Link, Truncate} from '@primer/react'
import {Tooltip} from '@primer/react/next'
import {useState} from 'react'
import {graphql, useFragment} from 'react-relay'

import {LABELS} from '../constants/labels'
import {VALUES} from '../constants/values'
import type {ReferencedEventInner$key} from './__generated__/ReferencedEventInner.graphql'
import styles from './ReferencedEventInner.module.css'
import {ReferencedEventVerificationStatus} from './ReferencedEventVerificationStatus'
import type {PreloadedSecondaryQueryType} from './secondary-timeline-query'

export type ReferencedEventInnerProps = {
  commitKey: ReferencedEventInner$key
  viewerLogin: string | null
  willCloseSubject: boolean
  subjectType: string
  eventId: string
  secondaryTimelineQueryRef?: PreloadedSecondaryQueryType
}

export function ReferencedEventInner({
  commitKey,
  willCloseSubject,
  viewerLogin,
  eventId,
  subjectType,
  secondaryTimelineQueryRef,
}: ReferencedEventInnerProps): React.ReactElement {
  const commit = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment ReferencedEventInner on Commit {
        message
        messageHeadlineHTML
        messageBodyHTML
        url
        abbreviatedOid
        repository {
          name
          owner {
            login
          }
          defaultBranch
        }
      }
    `,
    commitKey,
  )
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const [showCommitBody, setShowCommitBody] = useState(false)

  if (commit === null) {
    return <></>
  }

  // Guard against missing repository data (can happen with deleted/inaccessible repos)
  const repository = commit.repository
  const defaultBranch = repository?.defaultBranch ?? ''
  const willCloseMessage = defaultBranch
    ? LABELS.commitWillCloseMessage(subjectType, commit.abbreviatedOid, defaultBranch)
    : ''

  const hovercardAttributes = repository
    ? getHovercardAttributesForType('commit', {
        owner: repository.owner.login,
        repo: repository.name,
        commitish: commit.abbreviatedOid,
      })
    : undefined

  return (
    <div className={styles.referencedCommitContainer}>
      <div className={styles.commitHeaderRow}>
        <div className={styles.commitMetadata}>
          <Truncate title={commit.message} className={styles.commitMessageText}>
            {hovercardAttributes ? (
              <Link
                href={commit.url}
                target="_blank"
                {...hovercardAttributes}
                muted
                aria-label={commit.message}
                className={styles.commitHashLink}
              >
                <MarkdownViewer
                  verifiedHTML={
                    // we can be sure this is a SafeHTMLString because the concatenated content comes from a trusted
                    // GitHub graphql query
                    // eslint-disable-next-line github/unescaped-html-literal, @github-ui/github-monorepo/no-cast-as-safe-html-string
                    `<p style="font-family:monospace;font-size:12px">${commit.messageHeadlineHTML}</p>` as SafeHTMLString
                  }
                />
              </Link>
            ) : (
              <MarkdownViewer
                verifiedHTML={
                  // we can be sure this is a SafeHTMLString because the concatenated content comes from a trusted
                  // GitHub graphql query
                  // eslint-disable-next-line github/unescaped-html-literal, @github-ui/github-monorepo/no-cast-as-safe-html-string
                  `<p style="font-family:monospace;font-size:12px">${commit.messageHeadlineHTML}</p>` as SafeHTMLString
                }
              />
            )}
          </Truncate>
          {commit.messageBodyHTML && (
            <Button
              size="small"
              onClick={() => setShowCommitBody(!showCommitBody)}
              aria-label={showCommitBody ? 'Hide commit message body' : 'Show commit message body'}
              aria-expanded={showCommitBody}
              className={styles.toggleButton}
            >
              ...
            </Button>
          )}
        </div>
        <div className={styles.commitActions}>
          {willCloseSubject && defaultBranch && (
            <Tooltip text={willCloseMessage} type="label">
              <Link href={VALUES.closingViaCommitMessageUrl} muted>
                <InfoIcon />
              </Link>
            </Tooltip>
          )}
          <ReferencedEventVerificationStatus
            secondaryTimelineQueryRef={secondaryTimelineQueryRef}
            eventId={eventId}
            viewerLogin={viewerLogin}
          />
          <Link href={commit.url} muted className={styles.repoNameLink}>
            {commit.abbreviatedOid}
          </Link>
        </div>
      </div>
      {showCommitBody && (
        <MarkdownViewer
          verifiedHTML={
            // we can be sure this is a SafeHTMLString because the concatenated content comes from a trusted
            // GitHub graphql query
            // eslint-disable-next-line github/unescaped-html-literal, @github-ui/github-monorepo/no-cast-as-safe-html-string
            `<p style="font-family:monospace;font-size:12px;white-space:pre-wrap">${commit.messageBodyHTML}</p>` as SafeHTMLString
          }
        />
      )}
    </div>
  )
}
